'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useRef } from 'react'
import { pushToDataLayer } from '@/lib/site/gtm'
import { buildPageviewEvent } from '@/lib/site/pageview-tracking'

/**
 * Pushes a `pageview` event to the GTM dataLayer on every App Router
 * navigation. Required because GTM's built-in "All Pages" trigger only
 * fires on hard navigations; client-side route changes go undetected.
 *
 * GTM should be configured to use this custom event for page views and
 * the default "Page View" trigger should be disabled to avoid double
 * counting.
 *
 * Mounted via a `<Suspense fallback={null}>` boundary because
 * `useSearchParams` triggers Next.js's CSR-bailout requirement.
 */
export function GtmPageviewTracker() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const previousLocationRef = useRef<string | null>(null)

  useEffect(() => {
    if (!pathname) {
      return
    }
    const search = searchParams?.toString()
    const location = window.location.href

    if (previousLocationRef.current === location) {
      return
    }
    const previousLocation = previousLocationRef.current
    previousLocationRef.current = location

    pushToDataLayer(
      buildPageviewEvent({
        pathname,
        search: search ?? '',
        currentLocation: location,
        documentTitle: document.title,
        documentReferrer: document.referrer,
        previousLocation,
      })
    )
  }, [pathname, searchParams])

  return null
}
